import type { Comment } from './comment.type.ts';
import { TasResponseTag, APPROVAL_RESPONSE_TYPES } from './comment.constants';

export interface CommentExtension extends Comment {}
export class CommentExtension {
  // shorthand for whether this is a TAS approval comment
  get isTasApproval() {
    // this raises a glint warning when the tag isn't asserted to be a TasResponseTag
    // key, but a comment that's not related to TAS approval responses can definitely
    // have tags that aren't part of that set
    return (
      this.tags?.some((tag) => APPROVAL_RESPONSE_TYPES.has(<keyof typeof TasResponseTag>tag)) ??
      false
    );
  }

  /*********************************************
   **  TAS Approval Canned Response Modeling  **
   *********************************************/
  // deserialize TAS approval comment from JSON
  get tasApprovalNote(): { title: string; body: string } {
    if (!this.isTasApproval) {
      throw new Error('Comment is not TAS approval response');
    }

    const note = this.comment ? JSON.parse(this.comment) : { title: '', body: '' };

    // feels more ergonomic to alias "this" here & preserve access to it in
    // scope for the inline getters/setters below, rather than use Object's
    // static defineProperty method (which would allow the use of arrow
    // to keep "this" in scope)
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const record = this;

    // this is what we get for modeling this all here, but at least it *is* all here
    return {
      get title(): string {
        return note.title;
      },
      set title(title: string) {
        record.comment = JSON.stringify({ ...note, title });
      },
      get body(): string {
        return note.body;
      },
      set body(body: string) {
        record.comment = JSON.stringify({ ...note, body });
      },
    };
  }

  // serialize TAS approval comment to JSON
  set tasApprovalNote(comment: { title: string; body: string }) {
    if (!this.isTasApproval) {
      throw new Error('Comment is not TAS approval response');
    }
    this.comment = JSON.stringify(comment);
  }

  get tasApprovalTag(): string {
    return this.tags.find((tag) => APPROVAL_RESPONSE_TYPES.has(<keyof typeof TasResponseTag>tag))!;
  }

  // set a tag, idempotently ensuring that only one TAS response type tag exists in
  // the record's tags list while ignoring any other ones
  set tasResponseTag(tag: string) {
    if (!APPROVAL_RESPONSE_TYPES.has(<keyof typeof TasResponseTag>tag)) {
      throw new Error('Tag is not valid TAS approval tag');
    }
    const otherTags = this.tags.filter((currentTag) => {
      return !APPROVAL_RESPONSE_TYPES.has(<keyof typeof TasResponseTag>currentTag);
    });
    this.tags = [...otherTags, tag];
  }

  // look up the friendly response type label by tag
  get tasResponseType() {
    if (!this.isTasApproval) {
      throw new Error('Comment is not TAS approval response');
    }

    return APPROVAL_RESPONSE_TYPES.get(<keyof typeof TasResponseTag>this.tasApprovalTag!);
  }
}

const Registration = {
  kind: 'object',
  name: 'comment',
  features: CommentExtension,
};
export default Registration;
